import React, { useEffect, useMemo } from 'react';
import { CheckCircle, RotateCcw, FileText } from 'lucide-react';

export const ENTITY_TYPES = ['Private Limited Company', 'Limited Liability Partnership (LLP)', 'Partnership Firm', 'Sole Proprietorship', 'Public Limited Company'];

// Map free-text AI entity type (e.g. "Pvt Ltd") to one of the form's fixed options
export const matchEntityType = (raw) => {
  const t = (raw || '').toLowerCase().replace(/[^a-z ]/g, ' ');
  if (!t.trim()) return '';
  if (/\bllp\b|liability partnership/.test(t)) return ENTITY_TYPES[1];
  if (/\b(pvt|private)\b/.test(t)) return ENTITY_TYPES[0];
  if (/\b(public|plc)\b/.test(t) || /^\s*(ltd|limited)\s*$/.test(t)) return ENTITY_TYPES[4];
  if (/partnership/.test(t)) return ENTITY_TYPES[2];
  if (/proprietor|sole|individual/.test(t)) return ENTITY_TYPES[3];
  return '';
};

const VENDOR_FIELDS = [
  ['name', 'Vendor Name', 'span-2'], ['type', 'Entity Type', '', false, 'select'], ['gstin', 'GSTIN'],
  ['registered_office', 'Registered Office', 'span-2', true], ['representative_name', 'Signatory Name'],
  ['representative_designation', 'Signatory Designation'], ['pan', 'PAN'],
];

const TERM_FIELDS = [
  ['scope', 'Scope of Work', 'span-2', true], ['start_date', 'Start Date', '', false, 'date'],
  ['end_date', 'End Date', '', false, 'date'], ['monthly_value', 'Monthly Value (₹)', '', false, 'number'],
  ['gst_rate', 'GST Rate (%)', '', false, 'number'], ['pm_frequency', 'PM Frequency'],
  ['response_time', 'Response Time'], ['resolution_time', 'Resolution Time'],
  ['special_conditions', 'Special Conditions', 'span-2', true],
];

const Field = ({ id, label, value, onChange, span, multiline, type = 'text' }) => {
  const toValue = (v) => (type === 'number' && v !== '' ? Number(v) : v);
  const props = { value: value ?? '', onChange: (e) => onChange(toValue(e.target.value)), 'data-testid': `review-${id}` };
  const empty = value === '' || value === null || value === undefined || (type !== 'number' && !value);
  let control = <input type={type} {...props} />;
  if (multiline) control = <textarea rows={2} {...props} />;
  if (type === 'select') control = (
    <select {...props}>
      <option value="">Select entity type...</option>
      {ENTITY_TYPES.map((o) => <option key={o}>{o}</option>)}
    </select>
  );
  return (
    <div className={`review-field ${span || ''} ${empty ? 'empty' : ''}`}>
      <label>{label}</label>
      {control}
    </div>
  );
};

const DocPreview = ({ file }) => {
  const url = useMemo(() => URL.createObjectURL(file), [file]);
  useEffect(() => () => URL.revokeObjectURL(url), [url]);
  return (
    <div className="review-pane review-doc" data-testid="review-doc-preview">
      <div className="review-pane-head"><span><FileText className="inline w-4 h-4 mr-1" />{file.name}</span></div>
      {file.type === 'application/pdf' ? <iframe src={url} title="Proposal preview" /> : <img src={url} alt="Proposal preview" />}
    </div>
  );
};

export const ExtractionReview = ({ file, review, setReview, confidence, onCreate, onReset }) => {
  const setVendor = (k) => (v) => setReview((r) => ({ ...r, vendor: { ...r.vendor, [k]: v } }));
  const setTerm = (k) => (v) => setReview((r) => ({ ...r, terms: { ...r.terms, [k]: v } }));
  return (
    <div className="review-layout animate-fadeIn" data-testid="extraction-review">
      <DocPreview file={file} />
      <div className="review-pane">
        <div className="review-pane-head">
          <span>Review Extracted Details</span>
          <span className="confidence-badge" data-testid="review-confidence"><CheckCircle className="w-3 h-3" />{Math.round((confidence || 0) * 100)}% Confidence</span>
        </div>
        <div className="review-fields">
          <div className="review-section">Service Provider</div>
          <div className="review-grid">
            {VENDOR_FIELDS.map(([k, l, s, m, t]) => <Field key={k} id={`vendor-${k}`} label={l} span={s} multiline={m} type={t} value={review.vendor[k]} onChange={setVendor(k)} />)}
          </div>
          <div className="review-section">Contract Terms</div>
          <div className="review-grid">
            {TERM_FIELDS.map(([k, l, s, m, t]) => <Field key={k} id={`term-${k}`} label={l} span={s} multiline={m} type={t} value={review.terms[k]} onChange={setTerm(k)} />)}
          </div>
          <p className="field-hint" style={{ marginTop: 12 }}>Fields highlighted in amber were not found in the proposal. Check them against the document on the left.</p>
        </div>
        <div className="review-actions">
          <button className="btn btn-ghost" onClick={onReset} data-testid="review-reset-btn"><RotateCcw className="inline w-4 h-4 mr-1" />Upload Another</button>
          <button className="btn btn-primary" style={{ flex: 1 }} onClick={onCreate} data-testid="create-from-extraction">+ Create Work Order</button>
        </div>
      </div>
    </div>
  );
};
